import { PairingForm } from '../../components/PairingForm.js';
import { PairingQr } from '../../components/PairingQr.js';
import { getDashboardPairLabel } from '../../lib/dashboardPairLabel.js';
import { buildPairDeepLink } from '../../lib/getDashboardPublicUrl.js';
import { PairingCodeDigits } from './PairingCodeDigits.js';
import { PairingStatus } from './PairingStatus.js';
import { usePairingCountdown } from './usePairingCountdown.js';

type PairingWaitingProps = {
  sessionCode: string | null;
  sessionExpiresAt: string | null;
  sessionError: string | null;
  confirming: boolean;
  busy: boolean;
  error: string | null;
  onSubmit: (code: string) => Promise<void>;
  onClearError: () => void;
  onRefreshSession: () => void;
};

export function PairingWaiting({
  sessionCode,
  sessionExpiresAt,
  sessionError,
  confirming,
  busy,
  error,
  onSubmit,
  onClearError,
  onRefreshSession,
}: PairingWaitingProps) {
  const remaining = usePairingCountdown(sessionExpiresAt);
  const deepLink = sessionCode ? buildPairDeepLink(sessionCode) : null;
  const pairLabel = getDashboardPairLabel();

  return (
    <main className="pairing-main">
      <h1 className="pairing-main__title">Connect this screen with your phone or computer</h1>

      {sessionError ? (
        <div className="pairing-main__error-block">
          <p className="pairing-main__error" role="alert">
            {sessionError}
          </p>
          <button type="button" className="pairing-main__retry" onClick={onRefreshSession}>
            Get a new code
          </button>
        </div>
      ) : null}

      <div className="pairing-main__row">
        <div className="pairing-main__steps">
          <div className="pairing-step">
            <span className="pairing-step__n">1</span>
            <span className="pairing-step__text">
              Go to <strong>{pairLabel}</strong> on your phone or computer
            </span>
          </div>
          <div className="pairing-step pairing-step--code">
            <div className="pairing-step__head">
              <span className="pairing-step__n">2</span>
              <span className="pairing-step__text">Enter this code</span>
            </div>
            {sessionCode ? (
              <PairingCodeDigits code={sessionCode} />
            ) : (
              <p className="pairing-main__preparing">Preparing pairing code…</p>
            )}
          </div>
        </div>

        <div className="pairing-or" aria-hidden>
          <span className="pairing-or__line" />
          <span className="pairing-or__label">OR</span>
          <span className="pairing-or__line" />
        </div>

        <div className="pairing-qr-block">
          <div className="pairing-qr-block__frame">
            {deepLink ? (
              <PairingQr value={deepLink} size={520} />
            ) : (
              <div className="pairing-qr-block__skeleton" />
            )}
          </div>
          <span className="pairing-qr-block__caption">
            Scan with your
            <br />
            phone camera
          </span>
        </div>
      </div>

      {!sessionError ? (
        <PairingStatus confirming={confirming} remainingLabel={remaining} />
      ) : null}

      <details className="pairing-manual">
        <summary>Have a code from the dashboard instead?</summary>
        <PairingForm busy={busy} error={error} onSubmit={onSubmit} onClearError={onClearError} />
      </details>
    </main>
  );
}
